import { makeStyles } from '@material-ui/styles'
import Cookies from 'js-cookie'
import { prop } from 'ramda'
import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { DOMAIN_NAME } from 'app/constants'
import { saml2pf9Doc } from 'app/docLinks'
import type { RootState } from 'app/store'
import Alert from 'core/components/Alert'
import CodeBlock from 'core/components/CodeBlock'
import CopyToClipboard from 'core/components/CopyToClipboard'
import ExternalLink from 'core/components/ExternalLink'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import Button from 'core/elements/button'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useDataLoader from 'core/hooks/useDataLoader'
import type { Route } from 'core/plugins/route'
import { type SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import type Theme from 'core/themes/model'
import { isSystemUser } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { loadServiceCatalog } from 'k8s/components/apiAccess/endpoints/actions'
import { glanceNoProxyExport, UPLOAD_INTERFACE_LINE } from './glanceNoProxy'

const useStyles = makeStyles<Theme>((theme) => ({
  withUnits: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  unitsDropdown: {
    position: 'relative',
    bottom: 1,
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
  uploadVariants: {
    display: 'grid',
    gap: theme.spacing(1),
  },
}))

interface Props {
  addRoute: Route
}

// Single-store upload (the default): create the image directly in the one configured store.
const IMAGE_CREATE_CMD =
  'pcdctl image create --insecure --container-format bare --disk-format qcow2 ' +
  '--property os_type=[Linux | Windows | Other] [--public | --private] ' +
  '[--protected | --unprotected] [--property <key=value>] --file <image-file-path> <image-name>'

// Multi-store upload (when more than one image store is configured): upload once, then fan the
// image out to every configured store via --all-stores.
const IMAGE_UPLOAD_MULTISTORE_CMD =
  'pcdctl image upload --insecure --name <image-name> --file <image-file-path> ' +
  '--disk-format qcow2 [--public | --private] --all-stores ' +
  '[-- --property os_type=[Linux | Windows | Other] --protected]'

export default function ImportImageModal({ addRoute }: Props) {
  const { history, match } = useReactRouter()
  const { tab } = match.params

  const classes = useStyles()

  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const {
    userDetails: { name },
    features,
    domain,
    activeRegion,
    scopedToken,
  } = session

  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const isSSOUser = session?.userDetails?.federated?.length > 0
  const { prefs, updatePrefs } = useScopedPreferences()
  const { currentTenantName, currentRegion } = prefs
  const fqdn = window.location.host
  const currentDomain = domain?.name || Cookies.get(DOMAIN_NAME) || 'default'

  const stepOffset = isSSOUser ? 1 : 0
  const getStep = useCallback((base: number) => base + stepOffset, [stepOffset])

  const handleClose = () => {
    history.push(routeManager.routes.openstack.images.path({ tab }))
  }

  const [serviceCatalog] = useDataLoader(loadServiceCatalog)

  const noProxyBlock = glanceNoProxyExport(serviceCatalog)

  const OS_DETAILS = isSystemUser()
    ? `export OS_USERNAME=${name}
export OS_PASSWORD=<your-password>
export OS_AUTH_URL=https://${fqdn}/keystone/v3
export OS_AUTH_TYPE=password
export OS_IDENTITY_API_VERSION=3
export OS_REGION_NAME=${activeRegion || currentRegion}
export OS_USER_DOMAIN_NAME=${currentDomain}
export OS_PROJECT_DOMAIN_NAME=${currentDomain}
export OS_SYSTEM_SCOPE=all
${UPLOAD_INTERFACE_LINE}`
    : `export OS_USERNAME=${name}
export OS_PASSWORD=<your-password>
export OS_AUTH_URL=https://${fqdn}/keystone/v3
export OS_AUTH_TYPE=password
export OS_IDENTITY_API_VERSION=3
export OS_REGION_NAME=${activeRegion || currentRegion}
export OS_USER_DOMAIN_NAME=${currentDomain}
export OS_PROJECT_DOMAIN_NAME=${currentDomain}
export OS_PROJECT_NAME=${currentTenantName}
${UPLOAD_INTERFACE_LINE}`

  const OS_DETAILS_V2 = (hideToken = true) => `export OS_AUTH_URL=https://${fqdn}/keystone/v3      
export OS_AUTH_TYPE=token                                                                                                      
export OS_IDENTITY_API_VERSION=3                                                                                                 
export OS_REGION_NAME=${
    activeRegion || currentRegion
  }                                                                                                       
export OS_PROJECT_DOMAIN_NAME=${currentDomain}
export OS_PROJECT_NAME=${currentTenantName}
export OS_TOKEN=${hideToken ? '**********' : scopedToken}
${UPLOAD_INTERFACE_LINE}`

  const osDetailsToShow = isSSOUser ? OS_DETAILS_V2() : OS_DETAILS
  const osDetailsToCopy = isSSOUser ? OS_DETAILS_V2(false) : OS_DETAILS

  return (
    <ModalForm
      route={addRoute}
      title={`Steps to Upload an Image to your Image Library`}
      onClose={handleClose}
      submitting={false}
      footerButtonAlignment="end"
      footer={
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      }
    >
      <>
        {isSSOUser && (
          <Alert className={classes.alert}>
            <Text variant="body2">
              <b>Note:</b> The token (OS_TOKEN) is only valid for 24 hours. After it expires, you’ll
              need to log in again to generate a new one. Alternatively, you can generate a token
              via the CLI using{' '}
              <code>
                <b>saml2pf9</b>
              </code>{' '}
              - follow the instructions provided{' '}
              <ExternalLink url={saml2pf9Doc}>
                <b>here.</b>
              </ExternalLink>
            </Text>
          </Alert>
        )}
        <FormFieldSection title="Export the pcdctl RC environment variables" step={getStep(0)}>
          <CopyToClipboard copyText={osDetailsToCopy}>
            <CodeBlock>{osDetailsToShow}</CodeBlock>
          </CopyToClipboard>
        </FormFieldSection>
        {!!noProxyBlock && (
          <FormFieldSection title="HTTP proxy">
            <Text variant="body2">
              If the machine where you run pcdctl is behind an HTTP proxy, run these lines in the
              same shell before uploading.
            </Text>
            <CopyToClipboard copyText={noProxyBlock}>
              <CodeBlock>{noProxyBlock}</CodeBlock>
            </CopyToClipboard>
          </FormFieldSection>
        )}
        <FormFieldSection title="Upload the image using following command" step={getStep(1)}>
          {isVmware ? (
            <CopyToClipboard
              copyText={`glance image-create --name &lt;image-name&gt; --file &lt;image-file-path&gt;
                --disk-format vmdk --container-format bare --property vmware_adaptertype="lsiLogic"
                --property vmware_disktype="sparse" --property vmware_ostype="ubuntu64Guest"
                [--property &lt;key=value&gt;]`}
            >
              <CodeBlock>
                glance image-create --name &lt;image-name&gt; --file &lt;image-file-path&gt;
                --disk-format vmdk --container-format bare --property vmware_adaptertype="lsiLogic"
                --property vmware_disktype="sparse" --property vmware_ostype="ubuntu64Guest"
                [--property &lt;key=value&gt;]
              </CodeBlock>
            </CopyToClipboard>
          ) : (
            <div className={classes.uploadVariants}>
              <Text variant="body2">
                <b>Single store (default)</b>
              </Text>
              <CopyToClipboard copyText={IMAGE_CREATE_CMD}>
                <CodeBlock>{IMAGE_CREATE_CMD}</CodeBlock>
              </CopyToClipboard>
              <Text variant="body2">
                <b>Multi-store</b> (when multiple image stores are configured) — use{' '}
                <code>pcdctl image upload</code> to upload the image and copy it to all configured
                stores:
              </Text>
              <CopyToClipboard copyText={IMAGE_UPLOAD_MULTISTORE_CMD}>
                <CodeBlock>{IMAGE_UPLOAD_MULTISTORE_CMD}</CodeBlock>
              </CopyToClipboard>
              <Text variant="body2">
                <code>--all-stores</code> copies the image to every configured Glance store after
                upload.
              </Text>
            </div>
          )}
        </FormFieldSection>
        <FormFieldSection title="Pre-requisites">
          <Text variant="body2">
            <b>pcdctl</b> needs to be installed for using the above CLI. If not already installed,
            please install it using below command:
          </Text>
          <CopyToClipboard
            copyText={`bash <(curl -s https://pcdctl.s3.us-west-2.amazonaws.com/pcdctl-setup)`}
          >
            <CodeBlock>
              {`bash <(curl -s https://pcdctl.s3.us-west-2.amazonaws.com/pcdctl-setup)`}
            </CodeBlock>
          </CopyToClipboard>
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
